import React, { useState, useEffect } from 'react';

// Polotno and third party libraries
import { observer } from 'mobx-react-lite';
import { SectionTab } from 'polotno/side-panel';
import type { StoreType } from 'polotno/model/store';
import type { TemplatesSection } from 'polotno/side-panel';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';

// Utils
import { DEFAULT_RESTRICTED_AREA_BG, RESTRICTED_AREA_COLOR_PALETTE } from '../../../utils/constants';

// Actions
import { SET_BLOCK_COLOR } from '../../../redux/actions/action-types';
import { success } from '../../../redux/actions/snackbarActions';

// Components
import Typography from '../../GenericUIBlocks/Typography';

// Icons
// @ts-ignore
import AddressBlockIcon from '../../../assets/images/templates/address-block-icon';

// styles
import './styles.scss';

type SideSection = typeof TemplatesSection;

interface BlockColorsSectionProps {
  store: StoreType;
}

const BlockColors: SideSection = {
  name: 'address-block',
  Tab: observer(
    (props: { store: StoreType; active: boolean; onClick: () => void }) => (
      <SectionTab name="Address Block" {...props} iconSize={20}>
        <AddressBlockIcon />
      </SectionTab>
    )
  ) as SideSection['Tab'],
  Panel: observer(({ store }: BlockColorsSectionProps) => {
    const dispatch: AppDispatch = useDispatch();

    const blockColor = useSelector(
      (state: RootState) => state.templates.blockColor
    );

    const product = useSelector(
      (state: RootState) => state.templates.product
    ) as Record<string, any>;

    const currentProductId = +product?.id;

    const [selectedColor, setSelectedColor] = useState(
      blockColor || DEFAULT_RESTRICTED_AREA_BG
    );

    useEffect(() => {
      if (blockColor && blockColor !== selectedColor) {
        setSelectedColor(blockColor);
      }
    }, [blockColor]);

    const handleColorChange = (color: string) => {

      setSelectedColor(color);
      const pages = store.pages;
      let updated = false;
      let isSnapPack = false;
      
      const basicRestrictedIds = [
        'figure-1',
        'figure-2',
        'figure-3',
      ]

      const snapPackRestrictedIds = [
        'recipient-address-area',
        'recipient-address-box',
        'return-address-box',
        'postage-indicia-box',
        'barcode-box',
      ]

      const restrictedIds = [
        ...basicRestrictedIds,
        ...snapPackRestrictedIds
      ];
      const updatedPages: any = [];

      let lastCustomFigures: any = [];
      let lastPageIndex = 0;

      pages.forEach((page, index) => {
        const pageData = page.toJSON();
        const children = pageData.children;
        lastPageIndex = index;

        // Remove previously added custom-figure-* elements
        const filteredChildren = children.filter(
          (el: any) => !restrictedIds.some(id => el.id === `custom-${id}`)
        );

        // Find restricted elements
        const restrictedFigures = restrictedIds
          .map((id) => children.find((el: any) => el.type === 'figure' && el.id === id))
          .filter(Boolean); // Remove undefined

        if (restrictedFigures.length) {
          restrictedFigures.forEach(el => {
            if (snapPackRestrictedIds.includes(el.id)) {
              isSnapPack = true;
              el.cornerRadius= 5;
              el.preFill = color;
              if (el.id === 'recipient-address-box') {
                el.opacity = 0;
              }
            } else if (basicRestrictedIds.includes(el.id)) {
              el.cornerRadius = 5;
            }
          });
        }

        let customFigures: any[] = [];

        if (restrictedFigures?.length > 0) {
          customFigures = restrictedFigures.map((el: any) => {
            const json = el.toJSON?.() || el;
            return {
              ...json,
              id: `custom-${json.id}`,
              type: json.type,
              fill: color,
              cornerRadius: 5,
              ...(isSnapPack && {
                custom: {
                  preFill: color,
                  replaceBg: true
                }
              })
            };
          });

          // Save for adding after store reload
          lastCustomFigures = customFigures;

          // Append to children
          if (isSnapPack || currentProductId === 2 || currentProductId === 21) {
            customFigures.forEach(fig => {
              const matchId = fig.id.replace("custom-", ""); // remove prefix
              const targetIndex = filteredChildren.findIndex((item: any) => item.id === matchId);

              if (targetIndex !== -1) {
                filteredChildren.splice(targetIndex + 1, 0, fig);
              }
            });
          } else {
            filteredChildren.splice(1, 0, ...customFigures);
          }
        }

        updatedPages.push({
          ...pageData,
          children: filteredChildren,
        });
      });

      // Load updated pages into store
      store.loadJSON({ pages: updatedPages });

      // Add custom elements back to the last modified page
      if (lastCustomFigures.length) {
        store.pages[lastPageIndex].addElement(lastCustomFigures);
        updated = true;
      }

      if (updated) {  
        dispatch({ type: SET_BLOCK_COLOR, payload: color });
        dispatch(success('Address block color updated successfully'));
      }
    };

    return (
      <div className="block-colors-section">
        <div className="block-colors-header">
          <Typography className="title">
            Optional: Choose a background color for the mailing address block.
          </Typography>
          <Typography className="description">
            These approved options enhance your mailer's look while maintaining
            USPS compliance.
          </Typography>
        </div>

        <div className="color-palette">
          {RESTRICTED_AREA_COLOR_PALETTE.map((color, index) => (
            <div
              key={index}
              className={`color-swatch ${selectedColor === color.value ? 'selected' : ''}`}
              style={{ backgroundColor: color.value }}
              onClick={() => handleColorChange(color.value)}
              title={color.name}
            />
          ))}
        </div>
      </div>
    );
  }),
};

export default BlockColors;
